import type {
  GalleryItem,
  MediaItemInterface
} from '@hypercodestudio/basler-components';

import { getIndexCustomAttributes } from './DecoratedProduct';
import { getCustomAttributeValue } from './getCustomAttributeValueFromMetadataCode';
import type { GenerateImageOptions } from '~/utils/generateImageObject';
import { generateImageObject } from '~/utils/generateImageObject';
import type {
  CommonProductDetailFragment,
  CommonProductFragment,
  CustomAttributeV2Fragment,
  ProductInterface
} from '~~/lib/Shop/generated/schema';
import {
  BRAND,
  CAMERA_CATEGORY,
  CAMERA_SERIES_CODE,
  MODEL_NAME,
  PRODUCT_IMAGE
} from '~/utils/shop/constants';
import { isDefined } from '~/utils/guards/isDefined';
import type { ProductImageType } from '~/types/ProductImageType';
import { isAttributeValue } from '~/utils/shop/guards/isAttributeValue';
import { extractCustomAttributes } from '~/utils/shop/extractCustomAttributes';

export function getProductImages(
  product:
    CommonProductFragment | CommonProductDetailFragment | ProductInterface
): ProductImageType[] {
  const customAttributes = extractCustomAttributes(product);
  const productImages =
    getProductImagesFromCustomAttributes(customAttributes) ?? [];

  return [...productImages].sort(
    (a, b) => Number(a.productimagesorting) - Number(b.productimagesorting)
  );
}

export type ProductImageOptionsResolver =
  GenerateImageOptions | ((imageIndex: number) => GenerateImageOptions);

export type ProductImageGalleryItem = Omit<GalleryItem, 'media'> & {
  media: Extract<MediaItemInterface, { type: 'image' }>;
};

export function getProductImage(
  product:
    CommonProductFragment | CommonProductDetailFragment | ProductInterface,
  options: ProductImageOptionsResolver = {},
  defaultImageUrl?: string
): ProductImageGalleryItem[] {
  const resolveOptions = (imageIndex: number): GenerateImageOptions =>
    typeof options === 'function' ? options(imageIndex) : options;
  const mappedCustomAttributes = getIndexCustomAttributes(
    extractCustomAttributes(product)
  );

  // Mirror the product card headline sources (see DecoratedProduct):
  // series falls back to the brand, the model name to the product name.
  const altTag = [
    getCustomAttributeValue(mappedCustomAttributes[CAMERA_SERIES_CODE]) ??
      getCustomAttributeValue(mappedCustomAttributes[BRAND]),
    getCustomAttributeValue(mappedCustomAttributes[MODEL_NAME]) ?? product.name,
    getCustomAttributeValue(mappedCustomAttributes[CAMERA_CATEGORY])
  ]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(' ');

  const productImages = getProductImages(product);

  const images = productImages.flatMap(
    (img, imageIndex): ProductImageGalleryItem[] => {
      const media = generateImageObject(
        { altTag, url: img.medialink },
        resolveOptions(imageIndex)
      );
      return media ? [{ media }] : [];
    }
  );
  if (images.length) {
    return images;
  }

  if (defaultImageUrl) {
    const media = generateImageObject(
      { altTag, url: defaultImageUrl },
      resolveOptions(0)
    );
    return media ? [{ media }] : [];
  }

  return [];
}

function getProductImagesFromCustomAttributes(
  customAttributes: ReadonlyArray<CustomAttributeV2Fragment>
): ProductImageType[] | undefined {
  const hit = customAttributes.find((x) => x?.code === PRODUCT_IMAGE);

  const productImage = isAttributeValue(hit) ? hit?.value : undefined;

  if (isDefined(productImage) && productImage !== '') {
    try {
      const parsedImage: ProductImageType[] = JSON.parse(productImage);
      const currentImgs = Object.values(parsedImage).filter(
        (img) => img.medialink !== ''
      );

      if (currentImgs.length > 0) {
        return currentImgs;
      }
    } catch (error) {
      console.warn(
        'failed to parse product images from json:',
        productImage,
        error
      );
    }
  }
}
